import { useEffect, useState } from 'react';
import type { ReactNode, MouseEvent, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { useClickOutside } from '../../hooks/useClickOutside';
import { type Style, mergeStyle } from './menu.module.css';

interface Props {
  open: boolean;
  anchor: RefObject<HTMLElement | null>;
  top?: number;
  right?: number;
  center?: boolean;
  align?: 'left' | 'center' | 'right';
  children: ReactNode;
  style?: Style;
  autoWith?: boolean;
  disabledClick?: boolean;
  autoUpdatePosition?: boolean;
  onClose?: () => void;
}

export function Menu(props: Props) {

  const {
    open,
    anchor,
    top = 4,
    right = 0,
    align = 'right',
    center = false,
    autoWith = false,
    disabledClick = false,
    autoUpdatePosition = false,
    children,
    style: customStyle,
    onClose,
  } = props;

  const style = mergeStyle(customStyle);
  const [ref] = useState<any>({});

  useEffect(() => {

    let intervalId: NodeJS.Timeout;

    if (open) {

      let lastPosition: number;

      intervalId = setInterval(() => {

        if (!anchor.current) return;

        const rect = anchor.current.getBoundingClientRect();
        const currentPosition = rect.y;

        if (currentPosition !== lastPosition || autoUpdatePosition) {
          updateMenuPosition();
        }

        lastPosition = currentPosition;

      }, 100);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [open, autoUpdatePosition]);

  useClickOutside(() => {
    onClose?.();
  }, ref, open, [onClose]);

  function handleMenuRef(menu: HTMLDivElement | null) {
    ref.current = menu;
    updateMenuPosition();
  }

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (!disabledClick) {
      onClose?.();
      event.stopPropagation();
    }
  }

  function updateMenuPosition() {

    const menu = ref.current as HTMLElement;
    const element = anchor.current;

    if (menu && element) {

      const elementRect = element.getBoundingClientRect();

      if (autoWith) {
        menu.style.width = `${elementRect.width}px`;
      }

      const menuRect = menu.getBoundingClientRect();
      const bodyWidth = document.body.clientWidth;
      const bodyHeight = document.body.clientHeight - 40;
      const menuBottom = elementRect.top + menuRect.height + 12;

      let menuLeft = elementRect.right - menuRect.width - right;

      if (center || align === 'center') {

        menuLeft = elementRect.left + (elementRect.width - menuRect.width) / 2;

      } else if (align === 'left') {

        menuLeft = elementRect.left - right;
      }

      menuLeft = Math.max(0, Math.min(menuLeft, bodyWidth - menuRect.width));
      
      menu.style.right = `${bodyWidth - menuLeft - menuRect.width}px`;
      
      menu.style.top = menuBottom < bodyHeight 
        ? `${elementRect.bottom + top}px`
        : `${elementRect.top - (menuRect.height + top)}px`;

      menu.style.visibility = 'visible';
    }
  }

  return createPortal(
    <CSSTransition
      in={open}
      unmountOnExit={true}
      classNames={style.animation}
      timeout={160}
      nodeRef={ref}>
      <div
        className={style.container}
        ref={handleMenuRef}
        onClick={handleClick}>
        {children}
      </div>
    </CSSTransition>, 
    document.body
  );
}
